import { MdAdd, MdClose } from 'react-icons/md';

type Tranche = { percentage: number; days: number };

// Free-form multi-tranche payment-schedule builder (N échéances, each with
// its own % and day-offset from the invoice date). Mirrors the backend's
// paymentScheduleSchema validation (store/validation.ts) so the error shown
// here matches what the save request would otherwise reject with.
const PaymentScheduleEditor = (props: {
    value: Tranche[];
    onChange: (rows: Tranche[]) => void;
    maxRows?: number;
}) => {
    const { value, onChange, maxRows = 5 } = props;
    const rows = value || [];
    const total = rows.reduce((sum, row) => sum + (Number(row.percentage) || 0), 0);
    const roundedTotal = Math.round(total * 100) / 100;
    const isValid = rows.length >= 2 && roundedTotal === 100;

    const updateRow = (index: number, patch: Partial<Tranche>) => {
        onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    };
    const removeRow = (index: number) => {
        onChange(rows.filter((_, i) => i !== index));
    };
    const addRow = () => {
        if (rows.length >= maxRows) return;
        onChange([...rows, { percentage: 0, days: 30 }]);
    };

    return (
        <div className="flex flex-col gap-2">
            {rows.map((row, i) => (
                <div key={i} className="flex items-center gap-2">
                    <input
                        type="number"
                        min={0}
                        max={100}
                        value={row.percentage}
                        onChange={(e) => updateRow(i, { percentage: Number(e.target.value) })}
                        className="w-20 rounded-xl border border-gray-200 px-2 py-1.5 text-sm text-navy-700 outline-none focus:border-toola-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
                    />
                    <span className="text-xs text-gray-400">% à</span>
                    <input
                        type="number"
                        min={0}
                        value={row.days}
                        onChange={(e) => updateRow(i, { days: Number(e.target.value) })}
                        className="w-20 rounded-xl border border-gray-200 px-2 py-1.5 text-sm text-navy-700 outline-none focus:border-toola-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
                    />
                    <span className="text-xs text-gray-400">jours</span>
                    <button
                        type="button"
                        onClick={() => removeRow(i)}
                        className="ml-1 rounded-lg p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
                    >
                        <MdClose className="h-4 w-4" />
                    </button>
                </div>
            ))}
            <div className="flex items-center gap-3">
                <button
                    type="button"
                    onClick={addRow}
                    disabled={rows.length >= maxRows}
                    className="flex items-center gap-1 text-xs font-medium text-toola-500 hover:underline disabled:opacity-40 disabled:no-underline"
                >
                    <MdAdd className="h-4 w-4" /> Ajouter une échéance
                </button>
                <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${
                        isValid
                            ? 'bg-green-50 text-green-700 ring-green-200'
                            : 'bg-red-50 text-red-700 ring-red-200'
                    }`}
                >
                    Total : {roundedTotal}%
                </span>
            </div>
            {!isValid && rows.length > 0 && (
                <p className="text-xs text-red-500">
                    {rows.length === 1
                        ? 'Un échéancier personnalisé doit avoir au moins 2 échéances'
                        : 'La somme des pourcentages doit être égale à 100%'}
                </p>
            )}
        </div>
    );
};

export default PaymentScheduleEditor;
